/**
 * IdentityPremiumPackageItem
 *
 * Specialized premium package card for the Identity Pro package.
 * Wraps PremiumPackageItem and adds verification status section.
 * Matches Flutter: lib/presentation/screens/premium/widgets/identity_premium_package_item.dart
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { PremiumPackageEntity } from '@/domain/entities/premiumPackage/premiumPackageEntity';
import { InAppPurchaseStatus, isActiveStatus } from '@/domain/enums/premiumPackages/InAppPurchaseStatus';
import type { IdentityVerificationStatus } from '@/domain/enums/premiumPackages/IdentityVerificationStatus';
import { PremiumPackageItem } from './PremiumPackageItem';
import { useExpauseLocalizations } from '@/presentation/locale/expauseLocalizations';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface IdentityPremiumPackageItemProps {
  package: PremiumPackageEntity;
  status: InAppPurchaseStatus;
  verificationStatus: IdentityVerificationStatus | null;
  onSubscribe: () => void;
  onGetVerified: () => void;
  onIdentityInfo: () => void;
}

export const IdentityPremiumPackageItem = ({
  package: pkg,
  status,
  verificationStatus,
  onSubscribe,
  onGetVerified,
  onIdentityInfo,
}: IdentityPremiumPackageItemProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { expauseLocalizations } = useExpauseLocalizations();

  const showVerificationSection = isActiveStatus(status);
  const verificationLabel = expauseLocalizations.getVerificationLabel(verificationStatus);
  const canGetVerified = verificationStatus === null || verificationStatus === undefined;

  return (
    <div>
      <PremiumPackageItem
        package={pkg}
        status={status}
        onSubscribe={onSubscribe}
        qaIdPrefix="premium-identity-package"
      />

      {/* Verification status section — shown only when purchased/restored */}
      {showVerificationSection && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${responsive.spacing.lg}px ${responsive.spacing.base}px 0`,
            gap: responsive.spacing.sm,
          }}
        >
          <div style={{ flex: 1 }} />

          {/* Verification status / Get Verified button */}
          <button
            {...qaAttr({ id: `premium-identity-verify-${pkg.productId}` })}
            onClick={canGetVerified ? onGetVerified : undefined}
            style={{
              background: 'none',
              border: 'none',
              cursor: canGetVerified ? 'pointer' : 'default',
              padding: 0,
            }}
          >
            <span
              style={{
                fontSize: responsive.font.base,
                color: theme.text.primary,
                textAlign: 'center',
              }}
            >
              {verificationLabel}
            </span>
          </button>

          {/* Info icon */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onIdentityInfo}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <AssetIcon
                path="/assets/images/question.png"
                width={responsive.icon.small}
                height={responsive.icon.small}
                color={theme.text.primary}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
